import {
  type Derived,
  deriveStatus,
  type Enrichment,
  type EventSource,
  type EventType,
  type Item,
  type LarderEvent,
  type NewItem,
  parseQuantity,
  todayIso,
} from "@larder/shared";
import {
  collection,
  doc,
  getCountFromServer,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Repository for items and the event log.
 *
 * The log is the truth; `item.status` is a cache written alongside it purely so
 * the list can be queried without reading every event. Anything the user SEES
 * is derived, so a stale cache can never show them the wrong thing — see
 * docs/engineering/data-model.md.
 */

type Stored<T> = Omit<T, "id">;

const itemsCol = (locId: string) => collection(db, "locations", locId, "items");
const eventsCol = (locId: string) => collection(db, "locations", locId, "events");

/** An item together with what its event log says about it right now. */
export interface TrackedItem extends Item {
  derived: Derived;
  events: LarderEvent[];
}

/**
 * A stored document, made to match the `Item` type it claims to be.
 *
 * Firestore has no schema, so a document written before a field existed simply
 * lacks it — and casting `d.data()` to `Item` is a LIE the compiler cannot
 * catch. Every item added before enrichment shipped has no `category`, `emoji`,
 * `size` or `nutrition`, so those read back as `undefined` while the type
 * promises `T | null`.
 *
 * That is not a cosmetic difference. `undefined` reaching `updateDoc` throws —
 * Firestore rejects it rather than ignoring the key — so editing any older item
 * failed at the write, which is exactly how it presented: a save button that
 * silently did nothing.
 *
 * This is the one place documents become objects, so it is the one place the
 * gap can be closed. New fields get their default HERE, and nothing downstream
 * has to know the field is younger than the data.
 */
function hydrate(id: string, d: Stored<Item>): Item {
  return {
    ...d,
    id,
    size: d.size ?? null,
    category: d.category ?? null,
    emoji: d.emoji ?? null,
    nutrition: d.nutrition ?? null,
    enrichedAt: d.enrichedAt ?? null,
  };
}

/**
 * Adds an item and its `added` event atomically.
 *
 * A batch is correct here, unlike household creation: these rules only call
 * `exists()` on a membership document that was committed long ago, so nothing
 * in the batch depends on anything else in the batch.
 */
/**
 * The stored shape of a new item.
 *
 * One builder for both entry points, because they were drifting: every field
 * added to `Item` had to be remembered in two places, and forgetting it in the
 * batch path meant a spoken shopping list silently produced items missing
 * fields the typed path had. Firestore has no schema to catch that.
 *
 * `size` is parsed from `sizeText` when the caller did not supply one, so a
 * label reading "1 L" becomes a usable quantity with no model call at all.
 */
function buildItem(uid: string, input: NewItem, now: number): Stored<Item> {
  return {
    name: input.name.replace(/\s+/g, " ").trim(),
    brand: input.brand ?? null,
    sizeText: input.sizeText ?? null,
    size: input.size ?? parseQuantity(input.sizeText) ?? null,
    qty: input.qty ?? 1,
    category: input.category ?? null,
    emoji: input.emoji ?? null,
    nutrition: input.nutrition ?? null,
    // Set only once enrichment has actually run — see enrichSaved().
    enrichedAt: null,
    storage: input.storage ?? "pantry",
    storageId: input.storageId ?? null,
    purchasedAt: now,
    expiryDate: input.expiryDate ?? null,
    expiryPrecision: input.expiryPrecision ?? (input.expiryDate ? "day" : "none"),
    expirySource: input.expirySource ?? (input.expiryDate ? "user_edited" : "none"),
    mfgDate: input.mfgDate ?? null,
    priceMinor: input.priceMinor ?? null,
    status: input.expiryDate ? "active" : "undated",
    resolvedAt: null,
    createdAt: now,
    createdByUid: uid,
  };
}

/**
 * How many things are still in this household — for the "remove this place"
 * warning. A server-side count, because the place being removed is usually
 * not the one on screen, so nothing local knows. Finished items are not
 * counted: history goes too, but "you will lose 3 packets of dal" is what
 * makes someone stop, and "you will lose 40 resolved entries" is not.
 */
export async function countActiveItems(locId: string): Promise<number> {
  const snap = await getCountFromServer(
    query(itemsCol(locId), where("status", "==", "active")),
  );
  return snap.data().count;
}

export async function addItem(locId: string, uid: string, input: NewItem): Promise<Item> {
  const ref = doc(itemsCol(locId));
  const now = Date.now();

  const item = buildItem(uid, input, now);

  const batch = writeBatch(db);
  batch.set(ref, item);
  batch.set(doc(eventsCol(locId)), {
    itemId: ref.id,
    type: "added" satisfies EventType,
    at: now,
    byUid: uid,
    source: "app" satisfies EventSource,
  });
  await batch.commit();

  return { id: ref.id, ...item };
}

/**
 * Adds several items and their `added` events in ONE batch.
 *
 * A spoken sentence is one intent — "these four things arrived" — so it should
 * land atomically. Half a bag appearing because the network dropped mid-loop is
 * worse than none of it.
 *
 * Firestore batches cap at 500 writes and this uses two per item, so the caller
 * is capped well below that by the transcript length limit.
 */
export async function addItems(
  locId: string,
  uid: string,
  inputs: NewItem[],
): Promise<number> {
  if (inputs.length === 0) return 0;
  const batch = writeBatch(db);
  const now = Date.now();

  for (const input of inputs) {
    const ref = doc(itemsCol(locId));
    batch.set(ref, buildItem(uid, input, now));
    batch.set(doc(eventsCol(locId)), {
      itemId: ref.id,
      type: "added" satisfies EventType,
      at: now,
      byUid: uid,
      source: "app" satisfies EventSource,
    });
  }

  await batch.commit();
  return inputs.length;
}

const STATUS_FOR: Partial<Record<EventType, Item["status"]>> = {
  ate: "consumed",
  tossed: "wasted",
  presumed: "presumed_wasted",
  still_here: "active",
};

/**
 * Records the user's answer.
 *
 * The event is the record; the status field is updated in the same batch only
 * so lists stay queryable. It is never read back as truth.
 */
export async function resolveItem(
  locId: string,
  uid: string,
  itemId: string,
  type: Extract<EventType, "ate" | "tossed" | "still_here" | "presumed">,
  opts: { source?: EventSource; bumpDays?: number } = {},
): Promise<void> {
  const now = Date.now();
  const batch = writeBatch(db);

  batch.set(doc(eventsCol(locId)), {
    itemId,
    type,
    at: now,
    byUid: uid,
    source: opts.source ?? "app",
    ...(opts.bumpDays === undefined ? {} : { bumpDays: opts.bumpDays }),
  });

  batch.update(doc(itemsCol(locId), itemId), {
    status: STATUS_FOR[type] ?? "active",
    // "Still here" un-resolves, so the timestamp has to be cleared too.
    resolvedAt: type === "still_here" ? null : now,
  });

  await batch.commit();
}

export async function editItem(
  locId: string,
  itemId: string,
  patch: Partial<
    Pick<
      Item,
      | "name"
      | "expiryDate"
      | "expiryPrecision"
      | "storage"
      | "storageId"
      | "qty"
      | "size"
      | "category"
      | "emoji"
      | "priceMinor"
    >
  >,
): Promise<void> {
  /**
   * Strip `undefined` before it reaches Firestore, which REJECTS it outright
   * rather than ignoring the key — one `undefined` anywhere in the object
   * throws and the whole save is lost.
   *
   * It is also the right semantics: in a patch, a key set to `undefined` means
   * "leave this alone", while `null` means "clear it". Passing the object
   * through unfiltered conflated the two and made an absent field fatal.
   */
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (v !== undefined) clean[k] = v;
  }
  if (patch.expiryDate) clean.expirySource = "user_edited";

  await updateDoc(doc(itemsCol(locId), itemId), clean);
}

/**
 * Writes back what enrichment found, WITHOUT overwriting the user.
 *
 * Enrichment lands seconds after a save, and by then the user may already have
 * corrected something — they are looking at the item they just added. A model
 * answer arriving late must never overwrite a human answer that arrived early;
 * that is the one behaviour guaranteed to make the feature feel like it is
 * fighting you.
 *
 * So each field is written only where the item still has nothing. `enrichedAt`
 * is always stamped, so a product that genuinely has no answer is not asked
 * about again on every load.
 */
export async function applyEnrichment(
  locId: string,
  item: Item,
  found: Enrichment,
): Promise<void> {
  const patch: Record<string, unknown> = { enrichedAt: Date.now() };

  if (item.category === null && found.category !== null) patch.category = found.category;
  if (item.emoji === null && found.emoji !== null) patch.emoji = found.emoji;
  if (item.nutrition === null && found.nutrition !== null) {
    patch.nutrition = found.nutrition;
  }
  // Size is the one the user is most likely to have touched, and the one a
  // printed label already answered — so it is only ever filled into a blank.
  if (item.size === null && found.quantity !== null) patch.size = found.quantity;

  await updateDoc(doc(itemsCol(locId), item.id), patch);
}

/**
 * Live view of everything in a household, with status recomputed from events.
 *
 * Items and events are watched separately and joined in memory. At household
 * scale (hundreds of items, thousands of events) this is far simpler than
 * maintaining a server-side projection, and it means the derivation that the UI
 * shows is the same pure function the tests cover.
 */
export function watchLarder(
  locId: string,
  timezone: string,
  onChange: (items: TrackedItem[], events: LarderEvent[]) => void,
  onError: (e: Error) => void,
): Unsubscribe {
  let items: Item[] | null = null;
  let events: LarderEvent[] | null = null;

  const emit = () => {
    if (!items || !events) return; // wait for both, or the list flickers empty
    const today = todayIso(timezone);
    const byItem = new Map<string, LarderEvent[]>();
    for (const e of events) {
      const list = byItem.get(e.itemId);
      if (list) list.push(e);
      else byItem.set(e.itemId, [e]);
    }
    const tracked = items.map((item) => {
      const own = byItem.get(item.id) ?? [];
      return {
        ...item,
        events: own,
        derived: deriveStatus({
          expiryDate: item.expiryDate,
          events: own,
          today,
          timezone,
        }),
      };
    });
    onChange(tracked, events);
  };

  const stopItems = onSnapshot(
    itemsCol(locId),
    { includeMetadataChanges: true },
    (snap) => {
      items = snap.docs.map((d) => hydrate(d.id, d.data() as Stored<Item>));
      emit();
    },
    onError,
  );

  const stopEvents = onSnapshot(
    query(eventsCol(locId), orderBy("at", "asc")),
    { includeMetadataChanges: true },
    (snap) => {
      events = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Stored<LarderEvent>) }));
      emit();
    },
    onError,
  );

  return () => {
    stopItems();
    stopEvents();
  };
}
